import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer'
import type { Company, PurchaseOrderWithItems, Supplier } from '../types'
import { formatPaymentTerms } from './orderShare'

const fmtMoney = (n: number) =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('pt-BR')

function makeStyles(primaryColor: string) {
  return StyleSheet.create({
    page: { padding: 36, fontSize: 10, fontFamily: 'Helvetica', color: '#111' },
    header: { borderBottomWidth: 2, borderBottomColor: primaryColor, borderBottomStyle: 'solid', paddingBottom: 12, marginBottom: 18 },
    brand: { fontSize: 18, fontWeight: 700, color: primaryColor },
    subtitle: { fontSize: 9, color: '#666', marginTop: 2 },
    meta: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14 },
    metaCol: { flexGrow: 1, paddingRight: 12 },
    metaLabel: { fontSize: 8, color: '#666', textTransform: 'uppercase' },
    metaValue: { fontSize: 11, fontWeight: 700, marginTop: 2 },
    table: { borderTopWidth: 1, borderTopColor: '#888', borderTopStyle: 'solid', borderBottomWidth: 1, borderBottomColor: '#888', borderBottomStyle: 'solid', marginTop: 10 },
    rowHead: { flexDirection: 'row', backgroundColor: primaryColor, padding: 6 },
    row: { flexDirection: 'row', padding: 6, borderBottomWidth: 0.5, borderBottomColor: '#ddd', borderBottomStyle: 'solid' },
    rowAlt: { backgroundColor: '#F7F7F7' },
    c1: { width: '20%', flexShrink: 0, fontSize: 9 },
    c2: { width: '34%', flexShrink: 0, fontSize: 9 },
    c3: { width: '10%', flexShrink: 0, textAlign: 'right', fontSize: 9 },
    c4: { width: '18%', flexShrink: 0, textAlign: 'right', fontSize: 9 },
    c5: { width: '18%', flexShrink: 0, textAlign: 'right', fontSize: 9, fontWeight: 700 },
    totalRow: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 14, paddingTop: 8, borderTopWidth: 1, borderTopColor: primaryColor, borderTopStyle: 'solid' },
    totalLabel: { fontSize: 11, fontWeight: 700, marginRight: 12 },
    totalValue: { fontSize: 13, fontWeight: 700, color: primaryColor },
    footer: { position: 'absolute', bottom: 30, left: 36, right: 36, fontSize: 8, color: '#888', textAlign: 'center', borderTopWidth: 0.5, borderTopColor: '#ddd', borderTopStyle: 'solid', paddingTop: 8 },
  })
}

interface Props {
  order: PurchaseOrderWithItems
  supplier: Supplier | null
  company: Company
}

type Styles = ReturnType<typeof makeStyles>

interface OrderPageProps {
  order: PurchaseOrderWithItems
  supplier: Supplier | null
  company: Company
  styles: Styles
  primaryColor: string
}

// Uma página de pedido (reutilizada pelo PDF individual e pelo consolidado).
function OrderPageSection({ order, supplier, company, styles, primaryColor }: OrderPageProps) {
  const logoSrc = company.logo_url ?? null
  return (
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {logoSrc && (
              <Image
                src={logoSrc}
                style={{ width: 48, height: 48, marginRight: 12, objectFit: 'contain' }}
              />
            )}
            <View>
              <Text style={styles.brand}>{company.name}</Text>
              <Text style={styles.subtitle}>
                {order.order_type === 'consignacao' ? 'Pedido de Consignação' : 'Pedido de Compra'} · ax-kanban
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.meta}>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Nº Pedido</Text>
            <Text style={styles.metaValue}>{order.order_number}</Text>
          </View>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Data</Text>
            <Text style={styles.metaValue}>{fmtDate(order.created_at)}</Text>
          </View>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Status</Text>
            <Text style={styles.metaValue}>{order.status}</Text>
          </View>
        </View>

        <View style={styles.meta}>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Fornecedor</Text>
            <Text style={styles.metaValue}>{supplier?.name ?? '—'}</Text>
            {supplier?.email && <Text style={styles.subtitle}>{supplier.email}</Text>}
            {supplier?.phone && <Text style={styles.subtitle}>{supplier.phone}</Text>}
          </View>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Lead Time</Text>
            <Text style={styles.metaValue}>
              {supplier?.lead_time_days ?? '—'} dias
            </Text>
          </View>
          <View style={styles.metaCol}>
            <Text style={styles.metaLabel}>Compra Mínima</Text>
            <Text style={styles.metaValue}>
              {supplier?.min_order != null ? fmtMoney(supplier.min_order) : '—'}
            </Text>
          </View>
        </View>

        {formatPaymentTerms(supplier) && (
          <View style={{ marginBottom: 14, padding: 8, backgroundColor: '#F0F5F0', borderLeftWidth: 3, borderLeftColor: primaryColor, borderLeftStyle: 'solid' }}>
            <Text style={styles.metaLabel}>Prazos de Pagamento</Text>
            <Text style={[styles.metaValue, { color: primaryColor }]}>
              {formatPaymentTerms(supplier)}
            </Text>
          </View>
        )}

        <View style={styles.table}>
          <View style={styles.rowHead}>
            <Text style={styles.c1}>SKU</Text>
            <Text style={styles.c2}>Produto</Text>
            <Text style={styles.c3}>Qtd</Text>
            <Text style={styles.c4}>Custo Unit.</Text>
            <Text style={styles.c5}>Subtotal</Text>
          </View>
          {order.purchase_order_items.map((item, idx) => (
            <View key={item.id} style={[styles.row, ...(idx % 2 === 1 ? [styles.rowAlt] : [])]}>
              <Text style={styles.c1}>{item.sku}</Text>
              <Text style={styles.c2}>{item.product_name}</Text>
              <Text style={styles.c3}>{item.quantity}</Text>
              <Text style={styles.c4}>{fmtMoney(item.unit_cost)}</Text>
              <Text style={styles.c5}>{fmtMoney(item.subtotal)}</Text>
            </View>
          ))}
        </View>

        <View style={styles.totalRow}>
          <Text style={styles.totalLabel}>TOTAL</Text>
          <Text style={styles.totalValue}>{fmtMoney(order.total_value)}</Text>
        </View>

        {order.notes && (
          <View style={{ marginTop: 18 }}>
            <Text style={styles.metaLabel}>Observações</Text>
            <Text style={{ marginTop: 4 }}>{order.notes}</Text>
          </View>
        )}

        <Text style={styles.footer}>
          Gerado por ax-kanban em {fmtDate(new Date().toISOString())}
        </Text>
      </Page>
  )
}

function PurchaseOrderPDF({ order, supplier, company }: Props) {
  const primaryColor = company.primary_color ?? '#647B45'
  const styles = makeStyles(primaryColor)
  return (
    <Document>
      <OrderPageSection order={order} supplier={supplier} company={company} styles={styles} primaryColor={primaryColor} />
    </Document>
  )
}

// PDF consolidado: um pedido por página, num único documento.
function CombinedOrdersPDF({ orders, company }: { orders: PurchaseOrderWithItems[]; company: Company }) {
  const primaryColor = company.primary_color ?? '#647B45'
  const styles = makeStyles(primaryColor)
  return (
    <Document>
      {orders.map(order => (
        <OrderPageSection
          key={order.id}
          order={order}
          supplier={order.suppliers}
          company={company}
          styles={styles}
          primaryColor={primaryColor}
        />
      ))}
    </Document>
  )
}

export async function generateOrderPdfBlob(props: Props): Promise<Blob> {
  return await pdf(<PurchaseOrderPDF {...props} />).toBlob()
}

export async function generateCombinedOrderPdfBlob(
  orders: PurchaseOrderWithItems[],
  company: Company,
): Promise<Blob> {
  return await pdf(<CombinedOrdersPDF orders={orders} company={company} />).toBlob()
}

export default PurchaseOrderPDF
